import type { TFunction } from 'i18next';
import type { AppRoute } from '@/types';

export interface BreadcrumbCrumb {
  id: string;
  label: string;
  href?: AppRoute;
}

interface BuildCrumbsParams {
  pathname: string;
  t: TFunction;
}

/**
 * Walks the URL path and emits one crumb per segment, mapping each to the
 * `common.topbar.breadcrumb.*` key. The final crumb has no `href` (current page).
 *
 * Deliberately no organisation crumb: pages are scoped server-side by the caller's
 * whole IAM identity, not by the auto-selected organisation, so prefixing its name
 * claimed a filter that wasn't applied (a domain user saw whichever org happened to
 * sort first — e.g. one literally named "Test").
 */
export function buildBreadcrumbCrumbs({ pathname, t }: BuildCrumbsParams): BreadcrumbCrumb[] {
  const crumbs: BreadcrumbCrumb[] = [];

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return crumbs;
  }

  const root = segments[0];
  switch (root) {
    case 'cpo-analytics': {
      if (segments.length === 1) {
        crumbs.push({ id: 'cpo-analytics', label: t('topbar.breadcrumb.cpoAnalytics') });
      } else {
        crumbs.push({
          id: 'cpo-analytics',
          label: t('topbar.breadcrumb.cpoAnalytics'),
          href: '/cpo-analytics',
        });
        const stationId = segments[1] ?? '';
        crumbs.push({ id: `cpo-station-${stationId}`, label: stationId });
      }
      break;
    }
    case 'qr-payment': {
      crumbs.push({ id: 'qr-payment', label: t('topbar.breadcrumb.qrPayment') });
      break;
    }
    case 'oda': {
      if (segments.length === 1) {
        crumbs.push({ id: 'oda', label: t('topbar.breadcrumb.oda') });
      } else {
        crumbs.push({ id: 'oda', label: t('topbar.breadcrumb.oda'), href: '/oda' });
        const eanId = decodeURIComponent(segments[1] ?? '');
        crumbs.push({ id: `oda-point-${eanId}`, label: eanId });
      }
      break;
    }
    case 'alerts': {
      crumbs.push({ id: 'alerts', label: t('topbar.breadcrumb.alerts') });
      break;
    }
    case 'helpdesk': {
      crumbs.push({ id: 'helpdesk', label: t('topbar.breadcrumb.helpdesk') });
      break;
    }
    case 'billing': {
      crumbs.push({ id: 'billing', label: t('topbar.breadcrumb.billing') });
      if (segments[1] === 'split-billing') {
        crumbs.push({
          id: 'billing-split-billing',
          label: t('topbar.breadcrumb.splitBilling'),
        });
      } else if (segments[1] === 'invoices') {
        crumbs.push({
          id: 'billing-invoices',
          label: t('topbar.breadcrumb.invoices'),
        });
      } else if (segments[1] === 'fees') {
        crumbs.push({
          id: 'billing-fees',
          label: t('topbar.breadcrumb.fees'),
        });
      }
      break;
    }
    default:
      break;
  }

  return crumbs;
}
